import { useQuery } from '@tanstack/react-query';
import { supabase } from '../services/supabase';

export type WebsiteAssessmentType = 'kingsmen_strengths' | 'spiritual_gifts';

export type AssessmentRanking = {
  categoryId: string;
  label: string;
  score: number;
  description: string;
};

export type AssessmentResults = {
  scores: Record<string, number>;
  rankings: AssessmentRanking[];
  topResults: AssessmentRanking[];
};

export interface WebsiteAssessmentSubmission {
  id: string;
  assessment_type: WebsiteAssessmentType;
  respondent_name: string;
  respondent_email: string;
  respondent_phone?: string | null;
  answers: Record<string, string>;
  results: AssessmentResults;
  created_at?: string;
}

export type WebsiteAssessmentFilters = {
  assessmentType?: WebsiteAssessmentType | 'all';
  search?: string;
  fromDate?: string;
  toDate?: string;
};

const ASSESSMENT_TYPE_LABELS: Record<WebsiteAssessmentType, string> = {
  kingsmen_strengths: 'KingsMen ManCave Strengths',
  spiritual_gifts: 'Spiritual Gifts',
};

export const getAssessmentTypeLabel = (type: WebsiteAssessmentType) =>
  ASSESSMENT_TYPE_LABELS[type] ?? type;

async function loadStaffAssessmentSubmissions(): Promise<WebsiteAssessmentSubmission[]> {
  const { data, error } = await supabase.rpc('list_website_assessment_submissions_for_staff');
  if (!error) {
    return (data as WebsiteAssessmentSubmission[]) ?? [];
  }

  // Fallback for environments that restored staff RLS but not the RPC yet.
  if (/function.*does not exist/i.test(error.message)) {
    const { data: rows, error: tableError } = await supabase
      .from('website_assessment_submissions')
      .select(
        'id, assessment_type, respondent_name, respondent_email, respondent_phone, answers, results, created_at',
      )
      .order('created_at', { ascending: false })
      .limit(500);

    if (tableError) throw tableError;
    return (rows as WebsiteAssessmentSubmission[]) ?? [];
  }

  throw error;
}

export const useWebsiteAssessmentSubmissions = (
  filters: WebsiteAssessmentFilters = {},
  enabled = true,
) => {
  return useQuery<WebsiteAssessmentSubmission[]>({
    queryKey: ['websiteAssessmentSubmissions', filters],
    queryFn: async () => {
      let rows = await loadStaffAssessmentSubmissions();

      if (filters.assessmentType && filters.assessmentType !== 'all') {
        rows = rows.filter((row) => row.assessment_type === filters.assessmentType);
      }

      if (filters.fromDate) {
        const fromMs = new Date(`${filters.fromDate}T00:00:00.000Z`).getTime();
        rows = rows.filter((row) => {
          if (!row.created_at) return false;
          return new Date(row.created_at).getTime() >= fromMs;
        });
      }

      if (filters.toDate) {
        const toMs = new Date(`${filters.toDate}T23:59:59.999Z`).getTime();
        rows = rows.filter((row) => {
          if (!row.created_at) return false;
          return new Date(row.created_at).getTime() <= toMs;
        });
      }

      const search = filters.search?.trim().toLowerCase();
      if (search) {
        rows = rows.filter((row) => {
          const haystack = [
            row.respondent_name,
            row.respondent_email,
            row.respondent_phone ?? '',
            getAssessmentTypeLabel(row.assessment_type),
          ]
            .join(' ')
            .toLowerCase();
          return haystack.includes(search);
        });
      }

      return rows;
    },
    enabled,
    staleTime: 60_000,
  });
};
